import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Badge,
    Card,
    Col,
    Container,
    Row,
} from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import SmartTable from '../../components/Smart/SmartTable';
import SmartTopTool from '../../components/Smart/SmartTopTool';
import SmartPagination from '../../components/Smart/SmartPagination';
import API from '../../api/axios';
import { useFormat } from '../../components/FormatContext';

const SEARCH_TYPE = [
    {
        key: 'companyName',
        label: 'Company Name',
    },
    {
        key: 'email',
        label: 'Email',
    },
];

const FILTER_OPTIONS = {
    status: [
        'PENDING',
        'APPROVED',
        'REJECTED',
    ],
};

const DEFAULT_FILTERS = {
    status: [],
    isActive: [],
};

const DEFAULT_SORT = {
    value: null,
    order: 'asc',
};

const DEFAULT_META = {
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 1,
};

const DEFAULT_PAGE = {
    page_number: 1,
    total_pages: 1,
};

const UnverifiedCompaniesList = () => {
    const {
        baseFormatters,
    } = useFormat();

    const [rows, setRows] =
        useState([]);

    const [meta, setMeta] =
        useState(DEFAULT_META);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState('');

    const [
        searchConditions,
        setSearchConditions,
    ] = useState([]);

    const [filters, setFilters] =
        useState(DEFAULT_FILTERS);

    const [sort, setSort] =
        useState(DEFAULT_SORT);

    const [page, setPage] =
        useState(DEFAULT_PAGE);

    const fetchList =
        useCallback(async () => {
            try {
                setLoading(true);
                setError('');

                const params =
                    new URLSearchParams();

                params.set(
                    'page',
                    String(
                        page.page_number ||
                        1,
                    ),
                );

                params.set(
                    'pageSize',
                    String(
                        meta.pageSize ||
                        10,
                    ),
                );

                searchConditions.forEach(
                    ({
                        type,
                        value,
                    }) => {
                        if (
                            value == null ||
                            String(value).trim() === ''
                        ) {
                            return;
                        }

                        params.set(
                            type,
                            String(value).trim(),
                        );
                    },
                );

                if (
                    Array.isArray(
                        filters.status,
                    ) &&
                    filters.status.length >
                    0
                ) {
                    params.set(
                        'status',
                        filters.status[0],
                    );
                }

                if (
                    Array.isArray(
                        filters.isActive,
                    ) &&
                    filters.isActive.length >
                    0
                ) {
                    params.set(
                        'isActive',
                        filters.isActive[0],
                    );
                }

                /*
                 * sort 暂时不传后端。
                 *
                 * 但仍把 sort 保留在依赖里，
                 * 因为 SmartTopTool 当前仍然可以改变它。
                 * 如果后端完全没有排序并且 UI 也不需要，
                 * 最好之后把 sort 整体删除。
                 */

                const queryString =
                    params.toString();

                const {
                    data,
                } =
                    await API.get(
                        `/supplier/registers?${queryString}`,
                    );

                console.log(
                    'Fetched unverified companies data:',
                    data,
                );

                /*
                 * 注意：
                 * 你原代码这里是 setRows(data || [])
                 *
                 * 如果 API 返回的是：
                 * {
                 *   data: [],
                 *   meta: {}
                 * }
                 *
                 * 那么应该使用 data.data。
                 *
                 * 为了兼容两种返回结构，这里都支持。
                 */
                const list =
                    Array.isArray(data)
                        ? data
                        : Array.isArray(
                            data?.data,
                        )
                            ? data.data
                            : [];

                setRows(list);

                const nextMeta =
                    data?.meta ||
                    DEFAULT_META;

                setMeta(nextMeta);

                const nextPage =
                    nextMeta.page ||
                    1;

                const nextTotalPages =
                    nextMeta.totalPages ||
                    1;

                setPage((prev) => {
                    if (
                        prev.page_number ===
                        nextPage &&
                        prev.total_pages ===
                        nextTotalPages
                    ) {
                        return prev;
                    }

                    return {
                        ...prev,
                        page_number:
                            nextPage,
                        total_pages:
                            nextTotalPages,
                    };
                });
            } catch (e) {
                setError(
                    e?.response?.data
                        ?.message ||
                    e?.message ||
                    '加载失败',
                );

                setRows([]);
            } finally {
                setLoading(false);
            }
        }, [
            page.page_number,
            meta.pageSize,
            searchConditions,
            filters.status,
            filters.isActive,
            sort,
        ]);

    const handleSetSearchConditions =
        useCallback(
            (valueOrUpdater) => {
                setSearchConditions(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) =>
                    prev.page_number === 1
                        ? prev
                        : {
                            ...prev,
                            page_number: 1,
                        },
                );
            },
            [],
        );

    const handleSetFilters =
        useCallback(
            (valueOrUpdater) => {
                setFilters(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) =>
                    prev.page_number === 1
                        ? prev
                        : {
                            ...prev,
                            page_number: 1,
                        },
                );
            },
            [],
        );

    const handleSetSort =
        useCallback(
            (valueOrUpdater) => {
                setSort(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage((prev) =>
                    prev.page_number === 1
                        ? prev
                        : {
                            ...prev,
                            page_number: 1,
                        },
                );
            },
            [],
        );

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const columns = useMemo(
        () => [
            {
                key: 'date',
                label: 'Date',
                render: (item) => (
                    <div className="d-flex align-items-center">
                        {item.date
                            ? baseFormatters.datetime.format(
                                new Date(
                                    item.date,
                                ),
                            )
                            : '—'}
                    </div>
                ),
            },
            {
                keys: [
                    'id',
                    'companyName',
                ],
                label:
                    'Company Name',
                render: (item) => (
                    <div className="d-flex align-items-center">
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip
                                    id={`tooltip-${item.id}`}
                                >
                                    ID:{' '}
                                    {item.id}
                                </Tooltip>
                            }
                        >
                            <span
                                style={{
                                    cursor:
                                        'pointer',
                                }}
                            >
                                {item.companyName ||
                                    '—'}
                            </span>
                        </OverlayTrigger>
                    </div>
                ),
            },
            {
                key: 'taxId',
                label: 'Tax ID',
                render: (item) =>
                    item.taxId ||
                    '—',
            },
            {
                keys: [
                    'phone',
                    'phonePrefix',
                    'email',
                ],
                label: 'Contact',
                render: (item) => (
                    <div>
                        <div className="d-flex align-items-center">
                            {item.phonePrefix ||
                                ''}{' '}
                            {item.phone ||
                                ''}
                        </div>

                        <div className="d-flex align-items-center">
                            {item.email ||
                                '—'}
                        </div>
                    </div>
                ),
            },
            {
                key: 'country',
                label: 'Country',
                render: (item) =>
                    item.country ||
                    '—',
            },
            {
                key: 'status',
                label:
                    'Reg. Status',
                render: (item) =>
                    item.status ? (
                        <Badge
                            bg={
                                {
                                    APPROVED:
                                        'success',
                                    PENDING:
                                        'warning',
                                    REJECTED:
                                        'danger',
                                }[
                                item
                                    .status
                                ] ||
                                'secondary'
                            }
                        >
                            {
                                item.status
                            }
                        </Badge>
                    ) : (
                        <span className="text-muted">
                            —
                        </span>
                    ),
            },
            {
                key: 'actions',
                label: 'Actions',
                render: (item) => (
                    <div className="d-flex gap-2 align-items-center">
                        <NavLink
                            to={`/unverified-companies/edit/${item.id}`}
                            className="btn btn-edit p-0"
                            title="Edit"
                        >
                            <FaEdit
                                size={20}
                                style={{
                                    marginLeft:
                                        '4px',
                                }}
                            />
                        </NavLink>
                    </div>
                ),
            },
        ],
        [baseFormatters],
    );

    return (
        <Container
            fluid
            className="d-flex flex-column"
        >
            <Row className="mb-4">
                <Col sm={8}>
                    <span className="fw-semibold">
                        Suppliers Brief
                    </span>
                </Col>

                <Col
                    sm={4}
                    className="text-end"
                >
                    <span className="fw-semibold">
                        <NavLink
                            to="/dashboard"
                            className="block-link"
                        >
                            Dashboard
                        </NavLink>{' '}
                        - Suppliers Brief
                    </span>
                </Col>
            </Row>

            <Row>
                <Col sm={12}>
                    <Card className="h-100 p-2">
                        <Card.Body className="px-2 pt-2 pb-5">
                            <Row>
                                <Col md={12}>
                                    <SmartTopTool
                                        searchType={
                                            SEARCH_TYPE
                                        }
                                        searchConditions={
                                            searchConditions
                                        }
                                        setSearchConditions={
                                            handleSetSearchConditions
                                        }
                                        filterOptions={
                                            FILTER_OPTIONS
                                        }
                                        filters={
                                            filters
                                        }
                                        setFilters={
                                            handleSetFilters
                                        }
                                        sort={
                                            sort
                                        }
                                        setSort={
                                            handleSetSort
                                        }
                                    />
                                </Col>

                                <Col md={12}>
                                    <SmartTable
                                        data={rows}
                                        columns={
                                            columns
                                        }
                                        loading={
                                            loading
                                        }
                                        emptyState={
                                            <div className="text-center py-5 text-muted">
                                                <p>
                                                    {error ||
                                                        'No matching suppliers found'}
                                                </p>
                                            </div>
                                        }
                                    />
                                </Col>

                                <Col
                                    md={12}
                                    className="d-flex justify-content-end"
                                >
                                    <SmartPagination
                                        page={
                                            page
                                        }
                                        setPage={
                                            setPage
                                        }
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default UnverifiedCompaniesList;